import { useMemo, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { Check, Eye, FileDown, Leaf, Plus, Printer } from 'lucide-react'

import type {
    ListTeaPurchasesParams,
    TeaPurchase,
} from '@/lib/api/tea-purchase-api'
import { useWorkflowTracking } from '@/hooks/use-workflow-tracking'
import { PageBackButton } from '@/components/common/PageBackButton'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { growerFilter } from '@/components/common/filters/grower-filter'
import { remoteSelectFilter } from '@/components/common/filters/remote-select-filter'
import { selectFilter } from '@/components/common/filters/select-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { RowDeleteButton } from '@/components/common/row-delete-button'
import { Tooltip } from '@/components/ui/tooltip'
import { STICKY_ACTIONS_CELL } from '@/components/ui/table-sticky'
import { FreshKgCell } from '@/components/tea-purchases/kg-cells'
import { TeaPurchaseFormDialog } from '@/components/tea-purchases/tea-purchase-form-dialog'
import { TeaPurchasePrintDialog } from '@/components/tea-purchases/tea-purchase-print-dialog'
import {
    TeaPurchaseStatusBadge,
    teaPurchaseStatus,
} from '@/components/tea-purchases/tea-purchase-status-badge'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useAuth } from '@/hooks/use-auth'
import { useMinWidth } from '@/hooks/use-min-width'
import {
    useApproveTeaPurchase,
    useDeleteTeaPurchase,
    useExportTeaPurchases,
    useTeaPurchasesList,
} from '@/hooks/use-tea-purchase-query'
import { useGrower } from '@/hooks/use-growers-query'
import { useActiveTeaTypes } from '@/hooks/use-tea-types-query'
import { formatVN } from '@/lib/format'
import {
    teaPurchaseDeleteBlockReason,
    teaPurchaseDeleteConfirm,
    teaPurchaseDeleteScope,
} from '@/lib/tea-purchase-delete-guard'
import { m } from '@/lib/paraglide/messages'
import { areaNames } from '@/lib/tea-purchase-labels'
import { TeaPatternSelectCell } from '@/components/tea-purchases/tea-pattern-select-cell'
import { TeaTypeSelectCell } from '@/components/tea-purchases/tea-type-select-cell'
import { WeighingBreakdownCell } from '@/components/tea-purchases/weighing-breakdown-cell'

/**
 * Bộ lọc khai một lần. `growerFilter` và `remoteSelectFilter` là hai factory
 * viết thêm cho ca lọc theo id — `core` không biết chúng tồn tại, đúng như hợp
 * đồng `FilterFieldDef` cho phép.
 */
const FILTERS = defineListFilters({
    grower: growerFilter({ label: m.tea_purchase_filter_grower }),
    tea_type: remoteSelectFilter({
        label: m.tea_purchase_filter_tea_type,
        allLabel: m.tea_purchase_filter_approve_all,
        useOptions: () => {
            const { data } = useActiveTeaTypes()
            return (data?.data ?? []).map((tt) => ({
                value: tt.id,
                label: tt.name,
            }))
        },
    }),
    approved: selectFilter({
        label: m.tea_purchase_filter_approve,
        defaultValue: 'all',
        options: () => [
            { value: 'all', label: m.tea_purchase_filter_approve_all() },
            { value: 'yes', label: m.tea_purchase_filter_approved() },
            { value: 'no', label: m.tea_purchase_filter_not_approved() },
        ],
    }),
    date_from: dateFilter({ label: m.tea_purchase_date_from }),
    date_to: dateFilter({ label: m.tea_purchase_date_to }),
})

export const Route = createFileRoute('/_app/_authed/_sidebar/tea-purchases/')({
    component: TeaPurchaseListPage,
    validateSearch: FILTERS.validateSearch,
})

function TeaPurchaseListPage() {
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('tea_purchases.write')
    // 768px = breakpoint `md` của Tailwind, đúng ngưỡng mà hai bố cục
    // trước đây đổi chỗ cho nhau.
    const isDesktop = useMinWidth(768)

    const navigate = useNavigate({ from: Route.fullPath })
    const urlSearch = Route.useSearch()

    // Chip cho bộ lọc hộ trồng cần TÊN, mà URL chỉ có id → tra runtime rồi
    // truyền vào; chưa tra được thì không sinh chip (xem growerFilter).
    const growerId =
        typeof urlSearch.grower === 'string' ? urlSearch.grower : undefined
    const { data: selectedGrower } = useGrower(growerId)
    const chipLabels = {
        grower: selectedGrower
            ? `${m.tea_purchase_filter_grower()}: ${selectedGrower.name}`
            : undefined,
    }

    const { values, barProps, page, setPage } = useListFilters({
        def: FILTERS,
        search: urlSearch,
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        chipLabels,
    })

    const [openCreate, setOpenCreate] = useState(false)
    const [printState, setPrintState] = useState<{
        mode: 'fresh' | 'multi'
        tp?: TeaPurchase
    } | null>(null)
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const toggleId = (id: string) =>
        setSelectedIds((s) => {
            const next = new Set(s)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    // Map bộ lọc → tham số API, tường minh ở màn vì mỗi màn map một kiểu.
    const params = useMemo<ListTeaPurchasesParams>(
        () => ({
            page,
            per_page: 50,
            ...(values.grower ? { grower_id: values.grower } : {}),
            ...(values.tea_type !== 'all'
                ? { tea_type_id: values.tea_type }
                : {}),
            ...(values.approved !== 'all'
                ? { approved: values.approved === 'yes' }
                : {}),
            ...(values.date_from ? { date_from: values.date_from } : {}),
            ...(values.date_to ? { date_to: values.date_to } : {}),
        }),
        [values, page],
    )
    const { data, isLoading, isFetching, refetch } = useTeaPurchasesList(params)
    const exportExcel = useExportTeaPurchases()
    const items = data?.data ?? []
    const total = data?.meta?.total ?? 0
    const selectedRows = items.filter((n: TeaPurchase) => selectedIds.has(n.id))
    const printableCount = selectedRows.length

    return (
        <div className="space-y-4 page-pad">
            {/* Header: mobile stack (title trên, actions dưới), desktop row */}
            <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between md:gap-2">
                <div className="flex items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <Leaf className="size-5 md:size-6 text-primary" />
                    <h1 className="text-lg md:text-2xl font-semibold">
                        {m.tea_purchase_title()}
                    </h1>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={printableCount === 0}
                        onClick={() => setPrintState({ mode: 'multi' })}
                    >
                        <Printer className="size-4 mr-1" />
                        {m.tea_purchase_print_multi()}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={exportExcel.isPending}
                        onClick={() => exportExcel.mutate(params)}
                    >
                        <FileDown className="size-4 mr-1" />
                        {m.tea_purchase_export_excel()}
                    </Button>
                    {canWrite && (
                        <Button onClick={() => setOpenCreate(true)}>
                            <Plus className="size-4 mr-1" />
                            {m.tea_purchase_create()}
                        </Button>
                    )}
                </div>
            </div>
            <p className="text-sm text-muted-foreground">
                {m.tea_purchase_subtitle()}
            </p>

            <ListFilterBar
                {...barProps}
                onRefresh={() => void refetch()}
                refreshing={isFetching}
            />

            {/*
             * Chỉ dựng MỘT bố cục. Trước đây cả hai cùng nằm trong DOM và
             * chỉ ẩn bằng `hidden`/`md:hidden` — CSS giấu đi nhưng React vẫn
             * mount đủ, nên mỗi dòng phải dựng hai lần số ô chọn kèm hook của
             * chúng. Ở 50 dòng/trang đó là 100 ô vô hình cho mỗi lần tải màn.
             */}
            {isDesktop ? (
                <div className="rounded-md border bg-card overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead className="w-8" />
                                <TableHead>{m.tea_purchase_code()}</TableHead>
                                <TableHead>{m.tea_purchase_grower()}</TableHead>
                                <TableHead>
                                    {m.tea_purchase_tea_type()}
                                </TableHead>
                                <TableHead>{m.tea_purchase_areas()}</TableHead>
                                <TableHead>
                                    {m.tea_purchase_pattern()}
                                </TableHead>
                                <TableHead className="text-right">
                                    {m.tea_purchase_quantity()}
                                </TableHead>
                                <TableHead>
                                    {m.tea_purchase_weighings_title()}
                                </TableHead>
                                <TableHead>
                                    {m.tea_purchase_purchase_date()}
                                </TableHead>
                                <TableHead>
                                    {m.tea_purchase_status_col()}
                                </TableHead>
                                <TableHead
                                    className={`text-right ${STICKY_ACTIONS_CELL}`}
                                />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={13}
                                        className="py-8 text-center text-muted-foreground"
                                    >
                                        {m.common_loading()}
                                    </TableCell>
                                </TableRow>
                            ) : items.length === 0 ? (
                                <TableRow>
                                    <TableCell
                                        colSpan={13}
                                        className="py-8 text-center text-muted-foreground"
                                    >
                                        {m.tea_purchase_empty()}
                                    </TableCell>
                                </TableRow>
                            ) : (
                                items.map((n: TeaPurchase) => (
                                    <TableRow key={n.id} className="group">
                                        <TableCell>
                                            <input
                                                type="checkbox"
                                                className="size-4 align-middle"
                                                checked={selectedIds.has(n.id)}
                                                onChange={() => toggleId(n.id)}
                                            />
                                        </TableCell>
                                        <TableCell className="font-mono text-sm">
                                            <Link
                                                to="/tea-purchases/$id"
                                                params={{ id: n.id }}
                                                className="text-primary hover:underline"
                                            >
                                                {n.code}
                                            </Link>
                                            {n.source === 'smartlab' ? (
                                                <Badge
                                                    variant="outline"
                                                    className="ml-2"
                                                >
                                                    {m.tea_purchase_source_smartlab()}
                                                </Badge>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            {n.grower_name || '—'}
                                        </TableCell>
                                        <TableCell>
                                            <TeaTypeSelectCell
                                                row={n}
                                                canWrite={canWrite}
                                            />
                                        </TableCell>
                                        <TableCell className="max-w-48 truncate">
                                            {areaNames(n.areas)}
                                        </TableCell>
                                        <TableCell>
                                            <TeaPatternSelectCell
                                                row={n}
                                                canWrite={canWrite}
                                            />
                                        </TableCell>
                                        <TableCell className="text-right tabular-nums">
                                            <FreshKgCell row={n} />
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                                            <WeighingBreakdownCell
                                                weighings={n.smartlab_weighings}
                                            />
                                        </TableCell>
                                        <TableCell className="text-xs text-muted-foreground">
                                            {formatVN(n.purchase_date)}
                                        </TableCell>
                                        <TableCell>
                                            {/* Đã gộp phiếu khô: badge + link sang
                                            phiếu khô; còn lại nói chuyện duyệt
                                            (như cột "Phê duyệt" hệ cũ). */}
                                            {n.dried_tea_purchase_id ? (
                                                <Tooltip
                                                    content={m.dtp_merged_into()}
                                                >
                                                    <Link
                                                        to="/dried-tea-purchases/$id"
                                                        params={{
                                                            id: n.dried_tea_purchase_id,
                                                        }}
                                                        className="inline-flex items-center gap-1"
                                                        aria-label={m.dtp_merged_into()}
                                                    >
                                                        <Badge variant="secondary">
                                                            {n.dried_tea_purchase_code ||
                                                                m.dtp_merged_badge()}
                                                        </Badge>
                                                    </Link>
                                                </Tooltip>
                                            ) : n.approved_at ? (
                                                <Badge variant="default">
                                                    {m.tea_purchase_status_approved()}
                                                </Badge>
                                            ) : (
                                                <Badge variant="outline">
                                                    {m.tea_purchase_status_pending()}
                                                </Badge>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            className={`text-right ${STICKY_ACTIONS_CELL}`}
                                        >
                                            <div className="flex items-center justify-end gap-1">
                                                {teaPurchaseStatus(n) ===
                                                    'pending' && (
                                                    <TeaPurchaseApproveButton
                                                        row={n}
                                                        showLabel
                                                    />
                                                )}
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    asChild
                                                >
                                                    <Link
                                                        to="/tea-purchases/$id"
                                                        params={{ id: n.id }}
                                                    >
                                                        <Eye className="size-4" />
                                                    </Link>
                                                </Button>
                                                <Tooltip
                                                    content={m.tea_purchase_print_fresh()}
                                                >
                                                    <Button
                                                        size="sm"
                                                        variant="ghost"
                                                        aria-label={m.tea_purchase_print_fresh()}
                                                        onClick={() =>
                                                            setPrintState({
                                                                mode: 'fresh',
                                                                tp: n,
                                                            })
                                                        }
                                                    >
                                                        <Printer className="size-4" />
                                                    </Button>
                                                </Tooltip>
                                                {canWrite && (
                                                    <TeaPurchaseDeleteButton
                                                        row={n}
                                                    />
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <>
                    <div className="text-right text-sm text-muted-foreground">
                        {m.tea_purchase_total({ count: total })}
                    </div>
                    {/* Mobile: card list */}
                    <div className="space-y-2">
                        {isLoading ? (
                            <div className="py-8 text-center text-muted-foreground">
                                {m.common_loading()}
                            </div>
                        ) : items.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                                {m.tea_purchase_empty()}
                            </div>
                        ) : (
                            items.map((n: TeaPurchase) => (
                                <ListCard
                                    key={n.id}
                                    title={
                                        <span className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                className="size-4 shrink-0 align-middle"
                                                checked={selectedIds.has(n.id)}
                                                onChange={() => toggleId(n.id)}
                                                aria-label={n.code}
                                            />
                                            <Link
                                                to="/tea-purchases/$id"
                                                params={{ id: n.id }}
                                                className={
                                                    LIST_CARD_TITLE_CLASS
                                                }
                                            >
                                                {n.code}
                                            </Link>
                                        </span>
                                    }
                                    subtitle={n.grower_name || '—'}
                                    badge={
                                        <TeaPurchaseStatusBadge
                                            teaPurchase={n}
                                        />
                                    }
                                    actions={
                                        <>
                                            {teaPurchaseStatus(n) ===
                                            'pending' ? (
                                                <TeaPurchaseApproveButton
                                                    row={n}
                                                />
                                            ) : null}
                                            <Tooltip
                                                content={m.tea_purchase_print_fresh()}
                                            >
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    aria-label={m.tea_purchase_print_fresh()}
                                                    onClick={() =>
                                                        setPrintState({
                                                            mode: 'fresh',
                                                            tp: n,
                                                        })
                                                    }
                                                >
                                                    <Printer className="size-4" />
                                                </Button>
                                            </Tooltip>
                                            {canWrite && (
                                                <TeaPurchaseDeleteButton
                                                    row={n}
                                                />
                                            )}
                                        </>
                                    }
                                    metric={{
                                        label: m.tea_purchase_quantity(),
                                        value: <FreshKgCell row={n} />,
                                    }}
                                    fields={[
                                        {
                                            label: m.tea_purchase_tea_type(),
                                            value: (
                                                <TeaTypeSelectCell
                                                    row={n}
                                                    canWrite={canWrite}
                                                />
                                            ),
                                        },
                                        {
                                            label: m.tea_purchase_pattern(),
                                            value: (
                                                <TeaPatternSelectCell
                                                    row={n}
                                                    canWrite={canWrite}
                                                />
                                            ),
                                        },
                                        {
                                            label: m.tea_purchase_areas(),
                                            value: areaNames(n.areas),
                                        },
                                        {
                                            label: m.tea_purchase_weighings_title(),
                                            value: (
                                                <WeighingBreakdownCell
                                                    weighings={
                                                        n.smartlab_weighings
                                                    }
                                                />
                                            ),
                                        },
                                        {
                                            label: m.tea_purchase_purchase_date(),
                                            value: formatVN(n.purchase_date),
                                        },
                                    ]}
                                />
                            ))
                        )}
                    </div>
                </>
            )}

            <div className="flex items-center justify-between">
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                >
                    {m.common_prev_page()}
                </Button>
                <div className="text-sm text-muted-foreground">
                    {m.common_page()} {page}
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    disabled={page * 50 >= total}
                    onClick={() => setPage(page + 1)}
                >
                    {m.common_next_page()}
                </Button>
            </div>

            <TeaPurchaseFormDialog
                open={openCreate}
                onOpenChange={setOpenCreate}
            />
            <TeaPurchasePrintDialog
                open={!!printState}
                onOpenChange={(v) => !v && setPrintState(null)}
                mode={printState?.mode ?? 'fresh'}
                teaPurchase={printState?.tp ?? null}
                teaPurchases={
                    printState?.mode === 'multi' ? selectedRows : undefined
                }
            />
        </div>
    )
}

function TeaPurchaseApproveButton({
    row,
    showLabel = false,
}: {
    row: TeaPurchase
    showLabel?: boolean
}) {
    const approve = useApproveTeaPurchase()
    const workflow = useWorkflowTracking('tea_purchase_approve', {
        autoStart: false,
        attemptKey: row.id,
        operation: 'approve',
    })
    return (
        <Tooltip content={m.tea_purchase_action_approve()}>
            <Button
                size="sm"
                variant="outline"
                aria-label={m.tea_purchase_action_approve()}
                onClick={() => {
                    workflow.step('warning_shown')
                    if (!window.confirm(m.tea_purchase_approve_confirm())) {
                        workflow.finish('closed')
                        return
                    }
                    workflow.step('warning_confirmed')
                    void workflow
                        .run(
                            () => approve.mutateAsync(row.id),
                            (res) => res.data?.id,
                        )
                        .catch(() => {})
                }}
            >
                <Check className="size-4" />
                {showLabel && m.tea_purchase_action_approve()}
            </Button>
        </Tooltip>
    )
}

/**
 * Nút xoá 1 phiếu cân tươi. Phiếu đã gộp vào phiếu khô vẫn xoá được (gỡ khỏi
 * phiếu khô, phiếu khô hết nguồn thì xoá theo); chỉ khi phiếu khô đã vào lô
 * phân loại thì nút mờ đi, hover hiện đủ lý do thay vì biến mất.
 */
function TeaPurchaseDeleteButton({ row }: { row: TeaPurchase }) {
    const del = useDeleteTeaPurchase()
    const workflow = useWorkflowTracking('tea_purchase_delete', {
        autoStart: false,
        attemptKey: row.id,
        operation: 'delete',
    })
    return (
        <RowDeleteButton
            label={m.tea_purchase_action_delete()}
            blockedReason={teaPurchaseDeleteBlockReason(row)}
            confirmMessage={teaPurchaseDeleteConfirm(row)}
            pending={del.isPending}
            onConfirmShown={() => {
                workflow.setContext({
                    deleteScope: teaPurchaseDeleteScope(row),
                })
                workflow.step('warning_shown')
            }}
            onCancel={() => workflow.finish('closed')}
            onDelete={() => {
                workflow.step('warning_confirmed')
                void workflow.run(() => del.mutateAsync(row.id)).catch(() => {})
            }}
        />
    )
}
